import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../api';
import { formatDate, formatEuro, clienteNome, Modal, ConfirmDialog, LoadingSpinner } from '../components/shared';

const TIPI_AVVISO = ['Avviso bonario','Cartella esattoriale','Accertamento','Invito al contraddittorio','PVC','Atto di recupero','Altro'];

// ─── INPUT ANNO LIBERO ────────────────────────────────────────────────────────
function AnnoInput({ value, onChange, placeholder = 'Anno…', style = {} }) {
  return (
    <input
      type="number"
      min="1990" max="2099"
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ width: 90, textAlign: 'center', fontFamily: 'monospace', ...style }}
      className="form-control"
    />
  );
}
const MODELLI     = ['730','Redditi PF','Redditi SP','Redditi SC','IVA','IRAP','F24','770','Unico','Altro'];

const STATI_AVVISO = ['APERTO','CHIUSO','IN_LAVORAZIONE','SGRAVIO','SGRAVIO_PARZIALE','RATEIZZATO','PAGATO'];

const STATI_RATA_LIST = ['NON_PAGATA','PAGATA','SOSPESA','SCADUTA','PAGATA_IN_RITARDO'];
const STATO_RATA_LABEL = {
  NON_PAGATA:       'Non pagata',
  PAGATA:           'Pagata',
  SOSPESA:          'Sospesa',
  SCADUTA:          'Scaduta',
  PAGATA_IN_RITARDO:'Pagata in ritardo',
};
function statoRataLabel(s) { return STATO_RATA_LABEL[s] || s; }
function statoRataPill(s) {
  const map = {
    NON_PAGATA:       'pill-red',
    PAGATA:           'pill-green',
    SOSPESA:          'pill-gray',
    SCADUTA:          'pill-red',
    PAGATA_IN_RITARDO:'pill-amber',
  };
  return map[s] || 'pill-gray';
}

const STATO_AVVISO_LABEL = {
  APERTO:           'Aperto',
  CHIUSO:           'Chiuso',
  IN_LAVORAZIONE:   'In Lavorazione',
  SGRAVIO:          'Sgravio',
  SGRAVIO_PARZIALE: 'Sgravio Parziale',
  RATEIZZATO:       'Rateizzato',
  PAGATO:           'Pagato',
};

function statoAvvisoLabel(s) { return STATO_AVVISO_LABEL[s] || s; }

function statoAvvisoPill(s) {
  const map = {
    APERTO:           'pill-red',
    IN_LAVORAZIONE:   'pill-amber',
    RATEIZZATO:       'pill-blue',
    SGRAVIO_PARZIALE: 'pill-amber',
    PAGATO:           'pill-green',
    CHIUSO:           'pill-green',
    SGRAVIO:          'pill-green',
  };
  return map[s] || 'pill-gray';
}

// ─── SORTABLE TH ─────────────────────────────────────────────────────────────
function SortTh({ label, field, sort, dir, onSort, style={} }) {
  const active = sort === field;
  return (
    <th onClick={() => onSort(field)} style={{
      cursor:'pointer', userSelect:'none', whiteSpace:'nowrap',
      color: active ? 'var(--accent)' : undefined,
      background: active ? 'var(--accent-light)' : undefined,
      ...style
    }}>
      {label}{active ? (dir==='asc' ? ' ↑' : ' ↓') : ''}
    </th>
  );
}

// ─── CALCOLO DATA SCADENZA ────────────────────────────────────────────────────
function calcolaScadenza(dataRic, giorni) {
  if (!dataRic || !giorni) return '';
  const d = new Date(dataRic);
  d.setDate(d.getDate() + parseInt(giorni));
  return d.toISOString().slice(0,10);
}

function diffGiorni(data) {
  if (!data) return null;
  return Math.ceil((new Date(data) - new Date()) / 86400000);
}

// ─── UPLOAD ALLEGATI ─────────────────────────────────────────────────────────
function AllegatiSection({ avvisoId, allegatiStr, onRefresh, addToast }) {
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const [delItem, setDelItem]     = useState(null);
  const allegati = allegatiStr ? allegatiStr.split(',').filter(Boolean) : [];
  // Formato: driveId::nomeOriginale  oppure  path/locale
  const parseEntry = (f) => {
    if (f && f.includes('::')) {
      const [id, nome] = f.split('::');
      return { id, nome, url: 'https://drive.google.com/file/d/' + id + '/view' };
    }
    if (f && !f.includes('.') && f.length > 20) return { id: f, nome: f, url: 'https://drive.google.com/file/d/' + f + '/view' };
    return { id: f, nome: f.split('/').pop().replace(/^\d+_/, ''), url: '/uploads/' + f };
  };
  const fileUrl   = (f) => parseEntry(f).url;
  const cleanName = (f) => parseEntry(f).nome;
  const fileIcon  = f => { const e=(parseEntry(f).nome).split('.').pop().toLowerCase(); return e==='pdf'?'📄':['jpg','jpeg','png'].includes(e)?'🖼️':['doc','docx'].includes(e)?'📝':['xls','xlsx'].includes(e)?'📊':'📎'; };
  const isImg     = (f) => /\.(jpg|jpeg|png|gif|webp)$/i.test(cleanName(f));
  const isPdf     = (f) => /\.pdf$/i.test(cleanName(f)) || (!parseEntry(f).id.includes('.') && parseEntry(f).id.length > 20);

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try { await api.uploadAllegatoAvviso(avvisoId, file); addToast('Allegato caricato','success'); onRefresh(); }
    catch(e) { addToast(e.message,'error'); }
    finally { setUploading(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteAllegatoAvviso(avvisoId, delItem); addToast('Eliminato','success'); setDelItem(null); onRefresh(); }
    catch(e) { addToast(e.message,'error'); }
  };

  const [preview, setPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState([]);

  const handleMultiUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    setUploadProgress(Array.from(files).map(f => ({ name: f.name, done: false })));
    for (let i = 0; i < files.length; i++) {
      try {
        await api.uploadAllegatoAvviso(avvisoId, files[i]);
        setUploadProgress(p => p.map((x, j) => j === i ? { ...x, done: true } : x));
      } catch(e) { addToast('Errore: ' + files[i].name, 'error'); }
    }
    setUploading(false);
    setUploadProgress([]);
    onRefresh();
    addToast('Allegati caricati', 'success');
  };


  return (
    <div>
      {/* Drop zone multi-file */}
      <div style={{ border:'2px dashed var(--border-strong)', borderRadius:'var(--radius-sm)', padding:'16px', textAlign:'center', cursor:'pointer', background:'var(--bg)', marginBottom:12 }}
        onClick={() => fileRef.current.click()}
        onDragOver={e => e.preventDefault()}
        onDrop={e => { e.preventDefault(); handleMultiUpload(e.dataTransfer.files); }}>
        <input ref={fileRef} type="file" multiple style={{ display:'none' }}
          onChange={e => handleMultiUpload(e.target.files)} />
        <div style={{ fontSize:13, color:'var(--text-faint)' }}>
          {uploading
            ? uploadProgress.map((f,i) => <div key={i}>{f.done ? '✅' : '⏳'} {f.name}</div>)
            : '📎 Clicca o trascina per allegare uno o più documenti'}
        </div>
      </div>

      {/* Lista allegati con anteprima */}
      {allegati.map(f => (
        <div key={f} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius-sm)', marginBottom:6 }}>
          {/* Anteprima */}
          {isImg(f) ? (
            <img src={fileUrl(f)} alt="" onClick={() => setPreview(f)}
              style={{ width:36, height:36, objectFit:'cover', borderRadius:4, border:'1px solid var(--border)', cursor:'pointer' }} />
          ) : (
            <span style={{ fontSize:22, cursor:'pointer' }} onClick={() => setPreview(f)}>{fileIcon(f)}</span>
          )}
          <span style={{ flex:1, fontSize:13, fontWeight:500 }}>{cleanName(f)}</span>
          <button className="btn btn-sm" onClick={() => setPreview(f)}>👁</button>
          <a href={fileUrl(f)} target="_blank" rel="noreferrer" className="btn btn-sm">Apri</a>
          <button className="btn btn-sm btn-danger" onClick={() => setDelItem(f)}>×</button>
        </div>
      ))}
      {allegati.length === 0 && <div style={{ fontSize:12, color:'var(--text-faint)' }}>Nessun allegato</div>}

      {/* Modal anteprima */}
      {preview && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:2000, display:'flex', alignItems:'center', justifyContent:'center' }}
          onClick={() => setPreview(null)}>
          <div style={{ background:'white', borderRadius:8, padding:16, maxWidth:'90vw', maxHeight:'90vh', overflow:'auto' }}
            onClick={e => e.stopPropagation()}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:12 }}>
              <span style={{ fontWeight:600, fontSize:14 }}>{cleanName(preview)}</span>
              <button onClick={() => setPreview(null)} style={{ background:'none', border:'none', fontSize:20, cursor:'pointer' }}>×</button>
            </div>
            {isImg(preview) ? (
              <img src={fileUrl(preview)} alt="" style={{ maxWidth:'80vw', maxHeight:'75vh', objectFit:'contain' }} />
            ) : isPdf(preview) ? (
              <iframe src={fileUrl(preview)} style={{ width:'75vw', height:'75vh', border:'none' }} title="anteprima" />
            ) : (
              <div style={{ padding:40, textAlign:'center', color:'var(--text-faint)' }}>
                <div style={{ fontSize:48 }}>{fileIcon(preview)}</div>
                <div style={{ marginTop:12 }}>Anteprima non disponibile</div>
                <a href={fileUrl(preview)} target="_blank" rel="noreferrer" className="btn btn-primary" style={{ marginTop:12, display:'inline-block' }}>Apri file</a>
              </div>
            )}
          </div>
        </div>
      )}

      {delItem && <ConfirmDialog message={`Eliminare "${cleanName(delItem)}"?`} onConfirm={handleDelete} onCancel={() => setDelItem(null)} />}
    </div>
  );
}

// ─── IMPORT PDF RATE ─────────────────────────────────────────────────────────
function ImportRateModal({ avvisoId, onImported, onClose, addToast }) {
  const fileRef = useRef();
  const [loading, setLoading]   = useState(false);
  const [parsed, setParsed]     = useState(null);
  const [rate, setRate]         = useState([]);
  const [saving, setSaving]     = useState(false);

  const handleFile = async (file) => {
    if (!file) return;
    setLoading(true);
    try {
      const data = await api.importRateAvviso(avvisoId, file);
      setParsed(data);
      setRate(data.rate.map(r => ({ ...r, selezionata:true })));
    } catch(e) { addToast('Errore lettura PDF: '+e.message,'error'); }
    finally { setLoading(false); }
  };

  const rateOk = rate.filter(r => r.selezionata);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.saveRate(avvisoId, rateOk);
      addToast(`✅ Importate ${rateOk.length} rate`,'success');
      onImported();
      onClose();
    } catch(e) { addToast(e.message,'error'); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="Importa rate da PDF Agenzia Entrate" onClose={onClose} size="lg">
      {!parsed ? (
        <div>
          <div style={{ border:'2px dashed var(--border-strong)', borderRadius:'var(--radius)', padding:'36px 24px', textAlign:'center', cursor:'pointer', background:'var(--bg)' }}
            onClick={() => fileRef.current.click()}
            onDragOver={e=>e.preventDefault()}
            onDrop={e=>{ e.preventDefault(); handleFile(e.dataTransfer.files[0]); }}>
            <input ref={fileRef} type="file" accept=".pdf" style={{ display:'none' }} onChange={e=>handleFile(e.target.files[0])} />
            {loading ? <div style={{ fontSize:14 }}>⏳ Analisi PDF in corso…</div> : (
              <div>
                <div style={{ fontSize:40, marginBottom:10 }}>📄</div>
                <div style={{ fontSize:14, fontWeight:500 }}>Trascina il PDF del piano di rateazione</div>
                <div style={{ fontSize:12, color:'var(--text-faint)', marginTop:4 }}>Agenzia Entrate — Determinazione dei versamenti rateali</div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div>
          <div style={{ padding:'10px 14px', background:'var(--c-green-bg)', border:'1px solid var(--c-green)', borderRadius:'var(--radius-sm)', marginBottom:16, fontSize:13 }}>
            ✓ Rilevate <strong>{parsed.rate.length} rate</strong> · Totale {parsed.totale ? formatEuro(parsed.totale) : '—'}
          </div>
          <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginBottom:8 }}>
            <button className="btn btn-sm" onClick={() => setRate(r=>r.map(x=>({...x,selezionata:true})))}>Tutte</button>
            <button className="btn btn-sm" onClick={() => setRate(r=>r.map(x=>({...x,selezionata:false})))}>Nessuna</button>
          </div>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead><tr>
              {['','Rata','Scadenza','Capitale','Interessi','Totale'].map(h=><th key={h} style={{ padding:'8px 12px', background:'var(--bg)', borderBottom:'1px solid var(--border)', textAlign:'left', fontSize:11, textTransform:'uppercase', letterSpacing:'0.4px', color:'var(--text-faint)' }}>{h}</th>)}
            </tr></thead>
            <tbody>
              {rate.map((r,i) => (
                <tr key={i} style={{ opacity:r.selezionata?1:0.4 }}>
                  <td style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)' }}>
                    <input type="checkbox" checked={r.selezionata} onChange={() => setRate(prev=>prev.map((x,j)=>j===i?{...x,selezionata:!x.selezionata}:x))} style={{ accentColor:'var(--accent)', width:14, height:14, cursor:'pointer' }} />
                  </td>
                  <td style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', fontWeight:500 }}>#{r.rata}</td>
                  <td style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', fontSize:12 }}>{formatDate(r.data_scad)}</td>
                  <td style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', fontFamily:'monospace', fontSize:12 }}>{formatEuro(r.importo_capitale)}</td>
                  <td style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', fontFamily:'monospace', fontSize:12, color:r.importo_interessi>0?'var(--c-amber)':'var(--text-faint)' }}>{r.importo_interessi>0?formatEuro(r.importo_interessi):'—'}</td>
                  <td style={{ padding:'8px 12px', borderBottom:'1px solid var(--border)', fontFamily:'monospace', fontSize:13, fontWeight:600 }}>{formatEuro(r.importo_totale)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="modal-footer">
            <button className="btn" onClick={onClose}>Annulla</button>
            <button className="btn btn-primary" disabled={saving || !rateOk.length} onClick={handleSave}>
              {saving ? 'Salvataggio…' : `Importa ${rateOk.length} rate`}
            </button>
          </div>
        </div>
      )}
    </Modal>
  );
}

// ─── FORM AVVISO ─────────────────────────────────────────────────────────────
function AvvisoForm({ initial={}, clientiList, onSave, onClose, loading }) {
  const [d, setD] = useState({
    id_cliente:'', numero:'', data_ricezione:'', giorni_dilazione:'',
    data_scadenza:'', modello:'', anno_imposta:'', tipo:'Avviso bonario',
    stato:'APERTO', importo_totale:'', note:'', ...initial,
    id_cliente: initial.id_cliente||'',
    importo_totale: initial.importo_totale||'',
    giorni_dilazione: initial.giorni_dilazione||'',
  });
  const set = (k,v) => setD(p => ({ ...p, [k]:v }));

  // Auto-calcola data scadenza
  useEffect(() => {
    if (d.data_ricezione && d.giorni_dilazione) {
      set('data_scadenza', calcolaScadenza(d.data_ricezione, d.giorni_dilazione));
    }
  }, [d.data_ricezione, d.giorni_dilazione]);

  return (
    <form onSubmit={e => { e.preventDefault(); onSave(d); }}>
      <div className="form-grid">
        <div className="form-group form-full">
          <label className="form-label">Cliente</label>
          <select className="form-control" value={d.id_cliente} onChange={e => set('id_cliente', e.target.value)}>
            <option value="">— Seleziona cliente —</option>
            {[...clientiList].sort((a,b) => (a.denominazione||a.ragione_sociale||'').localeCompare(b.denominazione||b.ragione_sociale||'')).map(c => <option key={c.id} value={c.id}>{clienteNome(c)} ({c.codice})</option>)}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Tipologia</label>
          <select className="form-control" value={d.tipo} onChange={e => set('tipo', e.target.value)}>
            {TIPI_AVVISO.map(t => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Numero atto</label>
          <input className="form-control" value={d.numero||''} onChange={e => set('numero', e.target.value)} placeholder="Es. 123456789" />
        </div>

        <div className="form-group">
          <label className="form-label">Data ricezione</label>
          <input className="form-control" type="date" value={d.data_ricezione||''} onChange={e => set('data_ricezione', e.target.value)} />
        </div>
        <div className="form-group">
          <label className="form-label">Giorni di dilazione</label>
          <input className="form-control" type="number" min="0" value={d.giorni_dilazione} onChange={e => set('giorni_dilazione', e.target.value)} placeholder="Es. 30" />
        </div>

        <div className="form-group">
          <label className="form-label">Data scadenza pagamento</label>
          <input className="form-control" type="date" value={d.data_scadenza||''} onChange={e => set('data_scadenza', e.target.value)}
            style={{ background: d.data_scadenza && d.giorni_dilazione ? 'var(--accent-light)' : undefined }} />
          {d.data_ricezione && d.giorni_dilazione && d.data_scadenza && (
            <div style={{ fontSize:11, color:'var(--accent)', marginTop:3 }}>
              ↳ calcolata automaticamente ({d.giorni_dilazione} giorni da {formatDate(d.data_ricezione)})
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Importo totale (€)</label>
          <input className="form-control" type="number" step="0.01" value={d.importo_totale} onChange={e => set('importo_totale', e.target.value)} />
        </div>

        <div className="form-group">
          <label className="form-label">Modello</label>
          <select className="form-control" value={d.modello||''} onChange={e => set('modello', e.target.value)}>
            <option value="">— Seleziona —</option>
            {MODELLI.map(m => <option key={m}>{m}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label className="form-label">Anno d'imposta</label>
          <input className="form-control" type="number" min="1990" max="2099" value={d.anno_imposta||''} onChange={e => set('anno_imposta', e.target.value)} placeholder="Es. 2024" style={{fontFamily:'monospace'}} />
        </div>

        <div className="form-group">
          <label className="form-label">Stato pratica</label>
          <select className="form-control" value={d.stato} onChange={e => set('stato', e.target.value)}>
            {STATI_AVVISO.map(s => <option key={s} value={s}>{statoAvvisoLabel(s)}</option>)}
          </select>
        </div>

        <div className="form-group form-full">
          <label className="form-label">Note</label>
          <textarea className="form-control" value={d.note||''} onChange={e => set('note', e.target.value)} rows={3} />
        </div>
      </div>
      <div className="modal-footer" style={{ padding:'16px 0 0', borderTop:'1px solid var(--border)', marginTop:20 }}>
        <button type="button" className="btn" onClick={onClose}>Annulla</button>
        <button type="submit" className="btn btn-primary" disabled={loading}>{loading?'Salvataggio…':'Salva'}</button>
      </div>
    </form>
  );
}

// ─── RIGA RATA ───────────────────────────────────────────────────────────────
function RataRow({ r, i, onStato, onPagamento, avvisoId, addToast }) {
  const [editPag, setEditPag] = useState(false);
  const [dataPag, setDataPag] = useState(r.data_pagamento || '');
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);

  const handleSavePag = () => {
    onPagamento(r.id, dataPag, r.allegato);
    setEditPag(false);
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await fetch(`/api/avvisi/${avvisoId}/allegati`, { method:'POST', body:fd });
      const json = await res.json();
      if (json.success) {
        onPagamento(r.id, dataPag, json.data.filename);
        addToast('Allegato caricato','success');
      }
    } catch(e) { addToast('Errore upload','error'); }
    finally { setUploading(false); }
  };

  const isScaduta = r.stato === 'SCADUTA';
  const bg = i%2===0 ? 'var(--surface)' : 'var(--bg)';

  return (
    <div style={{ display:'grid', gridTemplateColumns:'50px 90px 110px 110px 120px 140px 110px 90px',
      gap:6, padding:'8px 18px', alignItems:'center',
      borderBottom:'1px solid var(--border)', background: bg }}>

      <div style={{ fontWeight:700, fontSize:13, color:'var(--accent)' }}>#{r.rata}</div>

      <div style={{ fontSize:12, color: isScaduta ? 'var(--c-red)' : 'inherit', fontWeight: isScaduta ? 600 : 400 }}>
        {formatDate(r.data_scad)}
      </div>

      <div style={{ fontFamily:'monospace', fontSize:12 }}>{formatEuro(r.importo_capitale)}</div>

      <div style={{ fontFamily:'monospace', fontSize:12, color:r.importo_interessi>0?'var(--c-amber)':'var(--text-faint)' }}>
        {r.importo_interessi>0 ? formatEuro(r.importo_interessi) : '—'}
      </div>

      <div style={{ fontFamily:'monospace', fontSize:13, fontWeight:600 }}>{formatEuro(r.importo_totale)}</div>

      {/* Stato */}
      <select className="form-control" style={{ padding:'3px 6px', fontSize:11, cursor:'pointer',
        color: statoRataPill(r.stato)==='pill-red'?'var(--c-red)':statoRataPill(r.stato)==='pill-green'?'var(--c-green)':'inherit',
        fontWeight:500 }}
        value={r.stato} onChange={e => onStato(r.id, e.target.value)}>
        {STATI_RATA_LIST.map(s => <option key={s} value={s}>{statoRataLabel(s)}</option>)}
      </select>

      {/* Data pagamento */}
      <div>
        {editPag ? (
          <div style={{ display:'flex', gap:3 }}>
            <input type="date" className="form-control" value={dataPag} onChange={e=>setDataPag(e.target.value)}
              style={{ padding:'2px 4px', fontSize:11, width:110 }} />
            <button className="btn btn-sm" style={{ padding:'2px 6px', fontSize:11 }} onClick={handleSavePag}>✓</button>
          </div>
        ) : (
          <div style={{ fontSize:11, cursor:'pointer', color: r.data_pagamento ? 'var(--c-green)' : 'var(--text-faint)' }}
            onClick={() => setEditPag(true)}>
            {r.data_pagamento ? formatDate(r.data_pagamento) : <span style={{textDecoration:'underline dotted'}}>+ data</span>}
          </div>
        )}
      </div>

      {/* Allegato */}
      <div>
        <input ref={fileRef} type="file" style={{ display:'none' }} onChange={e=>handleFileUpload(e.target.files[0])} />
        {r.allegato ? (
          <a href={`http://localhost:3001/uploads/${r.allegato}`} target="_blank" rel="noreferrer"
            style={{ fontSize:11, color:'var(--accent)' }}>📎 Doc</a>
        ) : (
          <button className="btn btn-sm" style={{ fontSize:10, padding:'2px 6px' }}
            onClick={() => fileRef.current.click()} disabled={uploading}>
            {uploading ? '…' : '📎'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── LISTA AVVISI ─────────────────────────────────────────────────────────────
export function AvvisiList({ addToast }) {
  const [rows, setRows]       = useState([]);
  const [clienti, setClienti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]             = useState('');
  const [statoF, setStatoF]   = useState('');
  const [sort, setSort]       = useState('data_ricezione');
  const [dir, setDir]         = useState('desc');
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving]   = useState(false);
  const navigate              = useNavigate();

  useEffect(() => { api.getClienti({ limit:300 }).then(d => setClienti(d.rows)); }, []);

  const handleSort = (field) => {
    if (sort === field) setDir(d => d==='asc'?'desc':'asc');
    else { setSort(field); setDir('asc'); }
  };

  const sortRows = (data) => {
    return [...data].sort((a, b) => {
      let va = a[sort], vb = b[sort];
      if (va == null) va = ''; if (vb == null) vb = '';
      const cmp = String(va).localeCompare(String(vb), 'it', {numeric:true});
      return dir === 'asc' ? cmp : -cmp;
    });
  };

  const load = useCallback(() => {
    setLoading(true);
    const p = {};
    if (q)      p.q     = q;
    if (statoF) p.stato = statoF;
    api.getAvvisi(p).then(data => setRows(data)).finally(() => setLoading(false));
  }, [q, statoF]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (data) => {
    setSaving(true);
    try { await api.createAvviso(data); addToast('Avviso creato','success'); setShowForm(false); load(); }
    catch(e) { addToast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const aperte    = rows.filter(r => r.stato==='APERTA').length;
  const scadute   = rows.filter(r => { const d=diffGiorni(r.data_scadenza); return d!==null && d<0 && r.stato!=='INVIATA'; }).length;

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <div className="page-title">Avvisi e Cartelle</div>
          <div className="page-subtitle">{rows.length} pratiche</div>
        </div>
        <button className="btn btn-primary" onClick={() => setShowForm(true)}>+ Nuovo Avviso</button>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:14, marginBottom:24 }}>
        {[['Pratiche totali', rows.length,'var(--accent)'],['Aperte',aperte,'var(--c-red)'],['Scadute',scadute,'var(--c-amber)']].map(([l,v,c])=>(
          <div key={l} className="kpi-card"><div className="kpi-accent" style={{background:c}}/><div className="kpi-label">{l}</div><div className="kpi-value">{v}</div></div>
        ))}
      </div>

      <div className="filters-bar">
        <div className="search-box">
          <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/><path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          <input placeholder="Cerca numero, modello, cliente…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <select className="form-control" style={{width:'auto'}} value={statoF} onChange={e => setStatoF(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {STATI_AVVISO.map(s => <option key={s} value={s}>{statoAvvisoLabel(s)}</option>)}
        </select>
      </div>

      <div className="card">
        <div className="table-wrap">
          {loading ? <LoadingSpinner /> : (
            <table>
              <thead><tr>
                <SortTh label="Cliente"       field="denominazione"  sort={sort} dir={dir} onSort={handleSort} />
                <SortTh label="Tipologia"     field="tipo"           sort={sort} dir={dir} onSort={handleSort} />
                <SortTh label="Numero"        field="numero"         sort={sort} dir={dir} onSort={handleSort} />
                <SortTh label="Ricezione"     field="data_ricezione" sort={sort} dir={dir} onSort={handleSort} />
                <th>Dilaz.</th>
                <SortTh label="Scadenza pag." field="data_scadenza"  sort={sort} dir={dir} onSort={handleSort} />
                <SortTh label="Modello"       field="modello"        sort={sort} dir={dir} onSort={handleSort} />
                <SortTh label="Anno imp."     field="anno_imposta"   sort={sort} dir={dir} onSort={handleSort} />
                <SortTh label="Importo"       field="importo_totale" sort={sort} dir={dir} onSort={handleSort} />
                <th>Rate</th>
                <SortTh label="Stato"         field="stato"          sort={sort} dir={dir} onSort={handleSort} />
              </tr></thead>
              <tbody>
                {sortRows(rows).map(a => {
                  const diff = diffGiorni(a.data_scadenza);
                  const scadColor = a.stato==='INVIATA' ? 'inherit' : diff!==null && diff<0 ? 'var(--c-red)' : diff!==null && diff<=30 ? 'var(--c-amber)' : 'inherit';
                  return (
                    <tr key={a.id} style={{ cursor:'pointer' }} onClick={() => navigate(`/avvisi/${a.id}`)}>
                      <td className="fw-500">{clienteNome(a)||'—'}</td>
                      <td><span className="pill pill-gray" style={{fontSize:10}}>{a.tipo}</span></td>
                      <td className="td-mono" style={{fontSize:11}}>{a.numero||'—'}</td>
                      <td style={{fontSize:12}}>{formatDate(a.data_ricezione)}</td>
                      <td style={{textAlign:'center'}}>{a.giorni_dilazione||'—'}</td>
                      <td style={{fontSize:12, color:scadColor, fontWeight:scadColor!=='inherit'?600:400}}>
                        {a.data_scadenza ? formatDate(a.data_scadenza) : '—'}
                        {diff!==null && diff>=0 && diff<=30 && a.stato!=='INVIATA' && <span style={{fontSize:10,marginLeft:4}}>({diff}gg)</span>}
                      </td>
                      <td>{a.modello||'—'}</td>
                      <td>{a.anno_imposta||'—'}</td>
                      <td style={{fontFamily:'monospace',fontSize:12}}>{a.importo_totale?formatEuro(a.importo_totale):'—'}</td>
                      <td style={{textAlign:'center',color:'var(--accent)',fontWeight:500}}>{a.n_rate||'—'}</td>
                      <td><span className={`pill ${statoAvvisoPill(a.stato)}`} style={{fontSize:11}}>{statoAvvisoLabel(a.stato)}</span></td>
                    </tr>
                  );
                })}
                {rows.length===0 && <tr><td colSpan={11}><div className="empty-state"><div className="empty-state-icon">⚠️</div><div className="empty-state-title">Nessun avviso o cartella</div></div></td></tr>}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showForm && (
        <Modal title="Nuovo Avviso / Cartella" onClose={() => setShowForm(false)} size="lg">
          <AvvisoForm clientiList={clienti} onSave={handleCreate} onClose={() => setShowForm(false)} loading={saving} />
        </Modal>
      )}
    </div>
  );
}

// ─── DETTAGLIO AVVISO ────────────────────────────────────────────────────────
export function AvvisoDetail({ addToast }) {
  const { id }               = useParams();
  const navigate             = useNavigate();
  const [data, setData]      = useState(null);
  const [clienti, setClienti]= useState([]);
  const [editing, setEditing]= useState(false);
  const [confirming, setConfirming] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [saving, setSaving]  = useState(false);
  const [tab, setTab]        = useState('rate');

  useEffect(() => { api.getClienti({ limit:300 }).then(d => setClienti(d.rows)); }, []);

  const load = () => api.getAvviso(id).then(setData).catch(() => navigate('/avvisi'));
  useEffect(() => { load(); }, [id]);

  const handleUpdate = async (formData) => {
    setSaving(true);
    try { await api.updateAvviso(id, formData); addToast('Aggiornato','success'); setEditing(false); load(); }
    catch(e) { addToast(e.message,'error'); }
    finally { setSaving(false); }
  };

  const handleDelete = async () => {
    try { await api.deleteAvviso(id); addToast('Eliminato','success'); navigate('/avvisi'); }
    catch(e) { addToast(e.message,'error'); }
  };

  const handleRataStato = async (rataId, stato) => {
    try { await api.patchRataStato(rataId, stato); load(); }
    catch(e) { addToast(e.message,'error'); }
  };

  const handleRataPagamento = async (rataId, data_pagamento, allegato) => {
    try {
      await fetch(`/api/avvisi_rate/${rataId}/pagamento`, {
        method:'PATCH', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ data_pagamento, allegato })
      });
      load();
    } catch(e) { addToast(e.message,'error'); }
  };

  if (!data) return <LoadingSpinner />;
  const { avviso: a, rate } = data;
  const diff = diffGiorni(a.data_scadenza);
  const scadColor = a.stato==='INVIATA' ? 'var(--c-green)' : diff!==null && diff<0 ? 'var(--c-red)' : diff!==null && diff<=30 ? 'var(--c-amber)' : 'var(--text-muted)';

  return (
    <div className="page-content">
      <div className="page-header">
        <div>
          <button className="btn btn-sm" onClick={() => navigate('/avvisi')} style={{ marginBottom:8 }}>← Avvisi e Cartelle</button>
          <div className="page-title">{a.tipo} {a.numero ? `n. ${a.numero}` : ''}</div>
          <div style={{ display:'flex', gap:6, marginTop:6, flexWrap:'wrap' }}>
            {a.modello && <span className="pill pill-gray">{a.modello}</span>}
            {a.anno_imposta && <span className="pill pill-gray">Anno {a.anno_imposta}</span>}
            <span className={`pill ${statoAvvisoPill(a.stato)}`}>{statoAvvisoLabel(a.stato)}</span>
          </div>
        </div>
        <div style={{ display:'flex', gap:8 }}>
          <button className="btn" onClick={() => setEditing(true)}>Modifica</button>
          <button className="btn btn-danger" onClick={() => setConfirming(true)}>Elimina</button>
        </div>
      </div>

      {/* Info card */}
      <div className="card" style={{ marginBottom:20 }}>
        <div className="card-header"><span className="card-title">Dati avviso</span></div>
        <div className="card-body">
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'14px 24px' }}>
            {[
              ['Cliente', clienteNome(a)],
              ['Numero atto', a.numero],
              ['Data ricezione', formatDate(a.data_ricezione)],
              ['Giorni dilazione', a.giorni_dilazione ? `${a.giorni_dilazione} giorni` : null],
              ['Scadenza pagamento', a.data_scadenza ? formatDate(a.data_scadenza) : null],
              ['Importo totale', a.importo_totale ? formatEuro(a.importo_totale) : null],
              ['Modello', a.modello],
              ['Anno d\'imposta', a.anno_imposta],
            ].filter(([,v])=>v).map(([l,v])=>(
              <div key={l}>
                <div style={{ fontSize:10.5, textTransform:'uppercase', letterSpacing:'0.4px', color:'var(--text-faint)', marginBottom:3 }}>{l}</div>
                <div style={{ fontSize:13.5, color: l==='Scadenza pagamento' ? scadColor : 'inherit', fontWeight: l==='Scadenza pagamento' ? 500 : 400 }}>{v}</div>
              </div>
            ))}
          </div>
          {diff !== null && a.stato !== 'INVIATA' && (
            <div style={{ marginTop:14, padding:'8px 14px', borderRadius:'var(--radius-sm)', fontSize:12.5, fontWeight:500,
              background: diff<0 ? '#FCEAEA' : diff<=30 ? '#FDF0DC' : 'var(--c-green-bg)',
              color: diff<0 ? 'var(--c-red)' : diff<=30 ? 'var(--c-amber)' : 'var(--c-green)' }}>
              {diff<0 ? `⚠ Scaduta da ${Math.abs(diff)} giorni` : diff===0 ? '⚠ Scade oggi!' : `📅 Scade tra ${diff} giorni`}
            </div>
          )}
          {a.note && <div style={{ marginTop:14, padding:'10px 14px', background:'var(--bg)', borderRadius:'var(--radius-sm)', fontSize:13, color:'var(--text-muted)' }}>📝 {a.note}</div>}
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', borderBottom:'1px solid var(--border)', marginBottom:16 }}>
        {[['rate',`Rate (${rate.length})`],['allegati','Allegati']].map(([key,label])=>(
          <button key={key} onClick={()=>setTab(key)}
            style={{ padding:'10px 18px', border:'none', borderBottom:`2px solid ${tab===key?'var(--accent)':'transparent'}`, background:'none', cursor:'pointer', fontSize:13.5, fontWeight:tab===key?500:400, color:tab===key?'var(--accent)':'var(--text-muted)' }}>
            {label}
          </button>
        ))}
      </div>

      {tab === 'rate' && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Piano rateazione</span>
            <button className="btn btn-sm btn-primary" onClick={() => setShowImport(true)}>📄 Importa da PDF</button>
          </div>
          {rate.length === 0 ? (
            <div className="empty-state" style={{ padding:'40px 0' }}>
              <div className="empty-state-icon">📅</div>
              <div className="empty-state-title">Nessuna rata</div>
              <div style={{ fontSize:13, color:'var(--text-faint)', marginTop:6 }}>
                Importa il piano di rateazione da PDF Agenzia Entrate
              </div>
              <button className="btn btn-primary" style={{ marginTop:16 }} onClick={() => setShowImport(true)}>
                📄 Importa da PDF
              </button>
            </div>
          ) : (
            <div>
              {/* Header */}
              <div style={{ display:'grid', gridTemplateColumns:'50px 90px 110px 110px 120px 140px 110px 90px', gap:6,
                padding:'7px 18px', background:'var(--bg)', fontSize:10, fontWeight:500, color:'var(--text-faint)',
                textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'1px solid var(--border)' }}>
                <div>Rata</div><div>Scadenza</div><div>Capitale</div><div>Interessi</div><div>Totale</div><div>Stato</div><div>Data pag.</div><div>Doc.</div>
              </div>
              {rate.map((r,i) => (
                <RataRow key={r.id} r={r} i={i} onStato={handleRataStato} onPagamento={handleRataPagamento} avvisoId={id} addToast={addToast} />
              ))}
              {/* Totale */}
              <div style={{ display:'grid', gridTemplateColumns:'60px 1fr 130px 110px 110px 120px',
                gap:8, padding:'10px 18px', borderTop:'2px solid var(--border)', background:'var(--bg)' }}>
                <div style={{ gridColumn:'1/5', fontSize:12, fontWeight:500, color:'var(--text-muted)' }}>Totale ({rate.length} rate)</div>
                <div style={{ fontFamily:'monospace', fontSize:12, color:'var(--c-amber)' }}>{formatEuro(rate.reduce((s,r)=>s+r.importo_interessi,0))}</div>
                <div style={{ fontFamily:'monospace', fontSize:14, fontWeight:700, color:'var(--accent)' }}>{formatEuro(rate.reduce((s,r)=>s+r.importo_totale,0))}</div>
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'allegati' && (
        <div className="card">
          <div className="card-header"><span className="card-title">Allegati</span></div>
          <div className="card-body">
            <AllegatiSection avvisoId={id} allegatiStr={a.allegati} onRefresh={load} addToast={addToast} />
          </div>
        </div>
      )}

      {editing && (
        <Modal title="Modifica Avviso" onClose={() => setEditing(false)} size="lg">
          <AvvisoForm initial={a} clientiList={clienti} onSave={handleUpdate} onClose={() => setEditing(false)} loading={saving} />
        </Modal>
      )}
      {confirming && <ConfirmDialog message={`Eliminare questo avviso e tutte le sue rate?`} onConfirm={handleDelete} onCancel={() => setConfirming(false)} />}
      {showImport && <ImportRateModal avvisoId={id} onImported={load} onClose={() => setShowImport(false)} addToast={addToast} />}
    </div>
  );
}


// ─── RATEAZIONI LIST (pagina separata) ───────────────────────────────────────
const STATI_RATA = [
  { value: 'NON_PAGATA', label: 'Non pagata', color: 'var(--c-red)' },
  { value: 'PAGATA', label: 'Pagata', color: 'var(--c-green)' },
  { value: 'PAGATA_IN_RITARDO', label: 'Pagata in ritardo', color: 'var(--c-amber)' },
  { value: 'SCADUTA', label: 'Scaduta', color: 'var(--c-red)' },
  { value: 'SOSPESA', label: 'Sospesa', color: 'var(--text-faint)' },
];

function clienteNomeR(r) {
  return r.denominazione || r.ragione_sociale || [r.nome, r.cognome].filter(Boolean).join(' ') || '—';
}

function formatDateR(d) {
  if (!d) return '—';
  const [y, m, dd] = d.split('-');
  return `${dd}/${m}/${y}`;
}

function formatEuroR(v) {
  if (!v && v !== 0) return '—';
  return Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' });
}

export function RateazioniList({ addToast }) {
  const [rows, setRows]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ]           = useState('');
  const [statoF, setStatoF] = useState('');
  const [delRata, setDelRata]   = useState(null);
  const [delPiano, setDelPiano] = useState(null);
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statoF) params.stato = statoF;
      if (q) params.q = q;
      const res = await fetch('/api/avvisi_rate/lista?' + new URLSearchParams(params), {
        headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
      });
      const j = await res.json();
      setRows(j.success ? (j.data || []) : []);
    } catch(e) { addToast && addToast('Errore caricamento', 'error'); }
    finally { setLoading(false); }
  }, [q, statoF]);

  useEffect(() => { load(); }, [load]);

  // Raggruppa per avviso
  const gruppi = React.useMemo(() => {
    const map = {};
    rows.forEach(r => {
      if (!map[r.id_avviso]) map[r.id_avviso] = { ...r, rate: [] };
      map[r.id_avviso].rate.push(r);
    });
    return Object.values(map);
  }, [rows]);

  const handleDeleteRata = async () => {
    try {
      const res = await fetch('/api/avvisi_rate/' + delRata.id, {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast && addToast('Rata eliminata', 'success');
      setDelRata(null); load();
    } catch(e) { addToast && addToast('Errore: ' + e.message, 'error'); }
  };

  const handleDeletePiano = async () => {
    try {
      const res = await fetch('/api/avvisi/' + delPiano.id_avviso + '/rate', {
        method: 'DELETE',
        headers: { Authorization: 'Bearer ' + (localStorage.getItem('sp_token') || '') }
      });
      const j = await res.json();
      if (!j.success) throw new Error(j.error);
      addToast && addToast('Piano rateazione eliminato', 'success');
      setDelPiano(null); load();
    } catch(e) { addToast && addToast('Errore: ' + e.message, 'error'); }
  };

  const toggle = (id) => setExpanded(e => ({ ...e, [id]: !e[id] }));

  return (
    <div className="page-content">
      {/* Header */}
      <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:14, flexWrap:'wrap' }}>
        <div style={{ fontSize:17, fontWeight:700, flex:1 }}>Piani di rateazione</div>
        <div className="search-box" style={{ width:200 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none">
            <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.5"/>
            <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          <input placeholder="Cerca cliente…" value={q} onChange={e => setQ(e.target.value)} />
        </div>
        <select className="form-control" style={{ width:'auto' }} value={statoF} onChange={e => setStatoF(e.target.value)}>
          <option value="">Tutti gli stati</option>
          {STATI_RATA_LIST.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        {(q || statoF) && <button className="btn btn-sm" style={{ color:'var(--c-red)' }} onClick={() => { setQ(''); setStatoF(''); }}>✕</button>}
      </div>

      {/* Totale */}
      <div style={{ fontSize:12, color:'var(--text-faint)', marginBottom:10 }}>
        {gruppi.length} piani · {rows.length} rate totali
      </div>

      {loading ? <div style={{ padding:32, textAlign:'center' }}>⏳ Caricamento…</div>
      : gruppi.length === 0 ? (
        <div className="card"><div className="empty-state"><div className="empty-state-icon">📋</div><div className="empty-state-title">Nessuna rateazione trovata</div></div></div>
      ) : (
        <div style={{ background:'var(--surface)', border:'1px solid var(--border)', borderRadius:'var(--radius)', overflow:'hidden' }}>
          {/* Header tabella */}
          <div style={{ display:'grid', gridTemplateColumns:'24px 1fr 100px 80px 80px 80px 64px',
            gap:8, padding:'7px 14px', background:'var(--bg)', borderBottom:'2px solid var(--border)',
            fontSize:10.5, fontWeight:700, color:'var(--text-faint)', textTransform:'uppercase', letterSpacing:'0.4px' }}>
            <div></div><div>Cliente / Avviso</div><div>Rate</div><div>Prossima</div><div>Importo</div><div>Stato</div><div></div>
          </div>

          {gruppi.map(g => {
            const nonPagate = g.rate.filter(r => !['PAGATA','PAGATA_IN_RITARDO'].includes(r.stato));
            const prossima  = nonPagate.sort((a,b) => (a.data_scad||'').localeCompare(b.data_scad||''))[0];
            return (
              <React.Fragment key={g.id_avviso}>
                <div style={{ display:'grid', gridTemplateColumns:'24px 1fr 100px 80px 80px 80px 64px',
                  gap:8, padding:'8px 14px', borderBottom:'1px solid var(--border)',
                  background:'var(--surface)', alignItems:'center', cursor:'pointer' }}
                  onClick={() => toggle(g.id_avviso)}>
                  <div style={{ fontSize:11, color:'var(--text-faint)', textAlign:'center' }}>{expanded[g.id_avviso] ? '▾' : '▸'}</div>
                  <div>
                    <div style={{ fontWeight:600, fontSize:13 }}>{clienteNomeR(g)}</div>
                    <div style={{ fontSize:11, color:'var(--text-faint)' }}>{g.numero || 'Avviso #' + g.id_avviso} · {g.tipo || ''}</div>
                  </div>
                  <div style={{ fontSize:12 }}>{g.rate.length} rate · {nonPagate.length} aperte</div>
                  <div style={{ fontSize:12 }}>{prossima ? formatDateR(prossima.data_scad) : '—'}</div>
                  <div style={{ fontSize:12, fontFamily:'monospace' }}>{prossima ? formatEuroR(prossima.importo_totale) : '—'}</div>
                  <div style={{ fontSize:11 }}>
                    {nonPagate.length === 0
                      ? <span style={{ color:'var(--c-green)', fontWeight:600 }}>Completato</span>
                      : <span style={{ color:'var(--c-amber)', fontWeight:600 }}>{nonPagate.length} aperte</span>}
                  </div>
                  <div style={{ display:'flex', gap:4 }} onClick={e => e.stopPropagation()}>
                    <button className="btn btn-sm btn-danger btn-icon" title="Elimina piano rateazione"
                      onClick={() => setDelPiano(g)}>🗑</button>
                  </div>
                </div>

                {expanded[g.id_avviso] && (
                  <div style={{ background:'var(--bg)', borderBottom:'1px solid var(--border)' }}>
                    {/* Header rate */}
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 90px 90px 110px 64px',
                      gap:8, padding:'6px 42px', fontSize:10.5, fontWeight:700, color:'var(--text-faint)',
                      textTransform:'uppercase', letterSpacing:'0.4px', borderBottom:'1px solid var(--border)' }}>
                      <div>Rata</div><div>Scadenza</div><div>Importo</div><div>Stato</div><div></div>
                    </div>
                    {g.rate.map(r => {
                      const sr = STATI_RATA_LIST.find(s => s.value === r.stato) || { label: r.stato, color: 'var(--text-faint)' };
                      return (
                        <div key={r.id} style={{ display:'grid', gridTemplateColumns:'1fr 90px 90px 110px 64px',
                          gap:8, padding:'7px 42px', borderBottom:'1px solid var(--border)',
                          background:'var(--surface)', alignItems:'center', fontSize:13 }}>
                          <div>Rata {r.rata || '—'}</div>
                          <div>{formatDateR(r.data_scad)}</div>
                          <div style={{ fontFamily:'monospace' }}>{formatEuroR(r.importo_totale)}</div>
                          <div>
                            <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:600,
                              background: sr.color + '22', color: sr.color }}>{sr.label}</span>
                          </div>
                          <div>
                            <button className="btn btn-sm btn-danger btn-icon" title="Elimina rata"
                              onClick={() => setDelRata(r)}>×</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      )}

      {/* Conferma elimina rata */}
      {delRata && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div className="card" style={{ maxWidth:400, width:'90%', padding:24 }}>
            <div style={{ fontWeight:600, marginBottom:12 }}>Eliminare la rata {delRata.rata}?</div>
            <div style={{ fontSize:13, color:'var(--text-faint)', marginBottom:20 }}>
              Scadenza: {formatDateR(delRata.data_scad)} · {formatEuroR(delRata.importo_totale)}
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-sm" onClick={() => setDelRata(null)}>Annulla</button>
              <button className="btn btn-sm btn-danger" onClick={handleDeleteRata}>Elimina</button>
            </div>
          </div>
        </div>
      )}

      {/* Conferma elimina piano */}
      {delPiano && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center' }}>
          <div className="card" style={{ maxWidth:400, width:'90%', padding:24 }}>
            <div style={{ fontWeight:600, marginBottom:12 }}>Eliminare l'intero piano di rateazione?</div>
            <div style={{ fontSize:13, color:'var(--text-faint)', marginBottom:20 }}>
              Cliente: {clienteNomeR(delPiano)} · {delPiano.rate?.length || 0} rate verranno eliminate
            </div>
            <div style={{ display:'flex', gap:8, justifyContent:'flex-end' }}>
              <button className="btn btn-sm" onClick={() => setDelPiano(null)}>Annulla</button>
              <button className="btn btn-sm btn-danger" onClick={handleDeletePiano}>Elimina tutto</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
